import { type ReactNode, type RefObject, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ImperativePanelHandle } from 'react-resizable-panels';
import { IconButton } from '@/Domain/components/Shared/Button/Button';
import { ResizablePanel } from '@/Domain/components/Shared/Resizable/Resizable';
import { usePanelRevealWidth } from '@/Domain/components/Shared/Resizable/usePanelRevealWidth';
import { PanelLeftCloseIcon } from '@/Domain/components/icons/PanelLeftCloseIcon';
import { PanelLeftOpenIcon } from '@/Domain/components/icons/PanelLeftOpenIcon';
import { cn } from '@/Domain/utils/cn';

// Shared left settings-panel surface; class exported so the mobile fallback matches.
export const SIDE_PANEL_CONTAINER = 'overflow-auto bg-card p-6 custom-scroll';

const SIDE_PANEL_DEFAULT_SIZE = 32;
const SIDE_PANEL_MIN_SIZE = 24;
const SIDE_PANEL_MAX_SIZE = 48;
// Collapsed width as % of the panel group; exported so the mockup sizes against the collapsed strip.
export const SIDE_PANEL_COLLAPSED_SIZE = 4;

type Props = {
  children: ReactNode;
  panelRef?: RefObject<ImperativePanelHandle>;
  onCollapsedChange?: (collapsed: boolean) => void;
};

export const PreviewSidePanel = ({ children, panelRef: externalRef, onCollapsedChange }: Props) => {
  const { t } = useTranslation('common');
  const internalRef = useRef<ImperativePanelHandle>(null);
  const panelRef = externalRef ?? internalRef;
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { measureRef, frozenWidth } = usePanelRevealWidth(isCollapsed);

  return (
    <ResizablePanel
      ref={panelRef}
      id="preview-side"
      order={1}
      defaultSize={SIDE_PANEL_DEFAULT_SIZE}
      minSize={SIDE_PANEL_MIN_SIZE}
      maxSize={SIDE_PANEL_MAX_SIZE}
      collapsible
      collapsedSize={SIDE_PANEL_COLLAPSED_SIZE}
      onCollapse={() => {
        setIsCollapsed(true);
        onCollapsedChange?.(true);
      }}
      onExpand={() => {
        setIsCollapsed(false);
        onCollapsedChange?.(false);
      }}
    >
      {isCollapsed && (
        <div className="flex h-full w-full justify-center border-r border-border bg-card pt-3">
          <IconButton
            variant="ghost"
            size="sm"
            onClick={() => panelRef.current?.expand()}
            aria-label={t('expand_panel')}
            title={t('expand_panel')}
          >
            <PanelLeftOpenIcon size={16} />
          </IconButton>
        </div>
      )}
      <div
        ref={measureRef}
        className={cn(
          'relative h-full w-full overflow-hidden border-r border-border bg-card',
          isCollapsed && 'hidden',
        )}
      >
        <IconButton
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => panelRef.current?.collapse()}
          aria-label={t('collapse_panel')}
          title={t('collapse_panel')}
          className="absolute right-3 top-3 z-10 rounded-md text-muted-foreground hover:text-foreground"
        >
          <PanelLeftCloseIcon size={16} />
        </IconButton>
        <div
          className={cn('ml-auto h-full overflow-auto p-6 custom-scroll', !frozenWidth && 'w-full')}
          style={{ scrollbarGutter: 'stable', width: frozenWidth }}
        >
          {children}
        </div>
      </div>
    </ResizablePanel>
  );
};
